import moment from 'moment';

import {
  ActivityActorType,
  ActivityCategory,
  ActivityEvent,
  ActivityFieldChange,
  ActivityOperation,
} from '../../api/activityApi/activityApi';
import { ActivityColumn, OPTIONAL_COLUMNS } from './ActivityColumnsMenu';

export type ActivityTab = 'users' | 'claimants' | 'system';

export const ACTIVITY_TABS: {
  value: ActivityTab;
  label: string;
  actorLabel: string;
  actorTypes: ActivityActorType[];
}[] = [
  {
    value: 'users',
    label: 'Users',
    actorLabel: 'User',
    actorTypes: ['FIRM_USER', 'FIRM_SERVICE_USER', 'ADMIN', 'VENDOR'],
  },
  { value: 'claimants', label: 'Claimants', actorLabel: 'Claimant', actorTypes: ['CUSTOMER'] },
  {
    value: 'system',
    label: 'System',
    actorLabel: 'Source',
    actorTypes: ['SYSTEM', 'INTEGRATION', 'UNKNOWN'],
  },
];

/** Entity chips of the filter panel, mapped to the audited tables behind them. */
export const ENTITY_OPTIONS: { label: string; subjectTypes: string[] }[] = [
  { label: 'Case', subjectTypes: ['Case'] },
  { label: 'Settlement', subjectTypes: ['Settlement', 'MemoDraft'] },
  { label: 'Deduction', subjectTypes: ['Deduction'] },
  { label: 'Client', subjectTypes: ['Client'] },
  { label: 'Claimant', subjectTypes: ['Customer'] },
  { label: 'Vendor', subjectTypes: ['Vendor', 'VendorFirmParticipation'] },
  {
    label: 'Bank account',
    subjectTypes: [
      'CustomerBankAccount',
      'VendorBankAccount',
      'TrustAccount',
      'UnrestrictedAccount',
    ],
  },
  { label: 'Firm', subjectTypes: ['Firm'] },
  { label: 'User', subjectTypes: ['FirmUser', 'FirmServiceUser'] },
  {
    label: 'Roles & permissions',
    subjectTypes: ['Role', 'Permission', 'RolePermission', 'UserRole', 'ServiceUserRole'],
  },
];

export const OPERATION_OPTIONS: { value: '' | ActivityOperation; label: string }[] = [
  { value: '', label: 'All activity' },
  { value: 'insert', label: 'Created' },
  { value: 'update', label: 'Updated' },
  { value: 'delete', label: 'Deleted' },
];

export const CATEGORY_LABELS: Record<ActivityCategory, string> = {
  AUTH: 'Sign-in',
  MUTATION: 'Change',
  TRANSACTION: 'Transaction',
  ADMIN: 'Administration',
  DOCUMENT: 'Document',
  EXPORT: 'Export',
  NAVIGATION: 'Page view',
  SYSTEM: 'System',
};

export const CATEGORY_OPTIONS: { value: '' | ActivityCategory; label: string }[] = [
  { value: '', label: 'All categories' },
  ...(Object.keys(CATEGORY_LABELS) as ActivityCategory[]).map((category) => ({
    value: category,
    label: CATEGORY_LABELS[category],
  })),
];

const ENTITY_NAMES: Record<string, string> = {
  Case: 'Case',
  Settlement: 'Settlement',
  MemoDraft: 'Settlement draft',
  Client: 'Client',
  Customer: 'Claimant',
  Vendor: 'Vendor',
  VendorFirmParticipation: 'Vendor',
  CustomerBankAccount: 'Claimant bank account',
  VendorBankAccount: 'Vendor bank account',
  TrustAccount: 'Trust account',
  UnrestrictedAccount: 'Operating account',
  Firm: 'Firm',
  FirmUser: 'User',
  FirmServiceUser: 'API service user',
  Role: 'Role',
  Permission: 'Permission',
  RolePermission: 'Role permission',
  UserRole: 'User role',
  ServiceUserRole: 'Service user role',
  ActivityEvent: 'Audit trail',
  SweepTransaction: 'Transaction',
};

/** Names of business events whose action name reads badly as a sentence. */
const ACTION_TITLES: Record<string, string> = {
  'firm.api-credential.created': 'API credentials created',
  'firm.api-credential.updated': 'API credentials updated',
  'firm.api-credential.deleted': 'API credentials deleted',
  'firm.api-credential.regenerated': 'API credentials regenerated',
  'onboarding.link.opened': 'Claimant opened the onboarding link',
  'onboarding.link.verified': 'Claimant signed in through the onboarding link',
  'onboarding.link.failed': 'Claimant could not sign in through the link',
  'onboarding.otp.sent': 'Sign-in code sent',
  'onboarding.otp.resent': 'Sign-in code sent again',
  'onboarding.otp.verified': 'Sign-in code verified',
  'onboarding.otp.failed': 'Sign-in code rejected',
  'onboarding.document.uploaded': 'Claimant uploaded identity documents',
  'onboarding.kyc.passed': 'Identity documents accepted (KYC)',
  'onboarding.kyc.failed': 'Identity documents not accepted (KYC)',
  'onboarding.consent.fbo-signed': 'Claimant signed the payout consent',
  'onboarding.consent.fbo-dda-signed': 'Claimant signed the V.law account consent',
  'onboarding.agreement-doc.viewed': 'Claimant opened an agreement document',
  'onboarding.address.updated': 'Claimant updated their address',
  'onboarding.contact-address.submitted': 'Claimant entered a mailing address',
  'onboarding.ssn-dob.submitted': 'Claimant entered SSN and date of birth',
  'onboarding.ssn-dob.skipped': 'Claimant skipped SSN and date of birth',
  'onboarding.payout-method.selected': 'Claimant chose a payout method',
  'onboarding.bank-account.linked': 'Claimant linked a bank account',
  'onboarding.bank-account.failed': 'Bank account link failed',
  'onboarding.passport-account.setup-started': 'Payout account setup started',
  'payout.funding.initiated': 'Payout funding started',
  'payout.check.stopped': 'Claimant stopped the payout check',
  'payout.check.reissued': 'Payout check reissued',
  'payout.ach.reverted': 'ACH payout account reverted',
  'onboarding.session.resume-requested': 'Claimant started reconnecting',
  'onboarding.session.resume-otp-sent': 'Reconnect code sent',
  'onboarding.session.resume-otp-resent': 'Reconnect code sent again',
  'onboarding.session.resume-otp-verified': 'Reconnect code verified',
  'onboarding.session.resume-otp-failed': 'Reconnect code rejected',
  'user.account.activated': 'User activated',
  'user.account.deactivated': 'User deactivated',
  'settlement.client-external-payment.retried': 'Settlement payment retried',
};

const OPERATION_VERBS: Record<string, string> = {
  insert: 'created',
  update: 'updated',
  delete: 'deleted',
  truncate: 'cleared',
};

const splitWords = (value: string) =>
  value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_.-]+/g, ' ')
    .trim();

const sentence = (value: string) => {
  const text = splitWords(value).toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const entityName = (subjectType: string | null) => {
  if (!subjectType) return '—';
  // The audit configuration tables (activity_audited_table, activity_column_policy).
  if (subjectType.startsWith('activity_')) return 'Audit settings';
  return ENTITY_NAMES[subjectType] ?? sentence(subjectType);
};

/** "case.update" -> "Case updated"; business actions like "settlement.approval.decided" -> "Settlement approval decided". */
export const activityTitle = (event: ActivityEvent) => {
  if (ACTION_TITLES[event.action]) return ACTION_TITLES[event.action];
  const [first, second, ...rest] = event.action.split('.');
  // Default names are "<table>.<operation>"; the stored operation gives the verb.
  const operation = event.operation?.toLowerCase();
  if (operation && OPERATION_VERBS[operation] && second === operation && rest.length === 0) {
    return `${entityName(event.subjectType)} ${OPERATION_VERBS[operation]}`;
  }
  // Custom actions start with the lower-cased table name, e.g. "firmuser.role.change_denied".
  const table = Object.keys(ENTITY_NAMES).find((name) => name.toLowerCase() === first);
  return table
    ? `${ENTITY_NAMES[table]} ${splitWords([second, ...rest].join(' ')).toLowerCase()}`
    : sentence(event.action);
};

/** Names of the event details the API sends (its FIRM_METADATA_KEYS), and of their values. */
const DETAIL_LABELS: Record<string, string> = { method: 'Payout method' };
const DETAIL_VALUES: Record<string, Record<string, string>> = {
  method: {
    ACH: 'Bank transfer (ACH)',
    'mail-check': 'Check by mail',
    DDA: 'V.law account (instant access)',
    FBO: 'V.law payout account (FBO)',
  },
};

/**
 * Second line under the activity: how many fields changed, else its details (e.g. "Payout
 * method: Check by mail"). Not the request path: it carries internal record ids, which the
 * firm view does not show.
 */
export const activitySubtitle = (event: ActivityEvent) => {
  if (event.changedFieldCount > 0) {
    return `${event.changedFieldCount} ${
      event.changedFieldCount === 1 ? 'field' : 'fields'
    } changed`;
  }
  return Object.entries(event.details ?? {})
    .map(([key, value]) => {
      const text = String(value);
      return `${DETAIL_LABELS[key] ?? sentence(key)}: ${DETAIL_VALUES[key]?.[text] ?? text}`;
    })
    .join(' · ');
};

/** Field names; the columns that point to another record read as that record. */
const FIELD_LABELS: Record<string, string> = {
  sol: 'Statute of limitations',
  tin: 'TIN',
  referenceId: 'Reference',
  firmId: 'Firm',
  primaryFirmId: 'Firm',
  clientId: 'Client',
  customerId: 'Claimant',
  caseId: 'Case',
  caseTypeId: 'Case type',
  defendantId: 'Defendant',
  defendantParticipationId: 'Defendant',
  settlementId: 'Settlement',
  trustAccountId: 'Trust account',
  destinationAccountId: 'Operating account',
  vendorId: 'Vendor',
  vendorFirmParticipationId: 'Vendor',
  userId: 'User',
  externalVendorId: 'Accounting id',
  accountingEntityId: 'Accounting record id',
};

/** "Case.closedAt" -> "Closed at" */
export const fieldLabel = (field: string) => {
  const column = field.split('.').pop() ?? field;
  return FIELD_LABELS[column] ?? sentence(column);
};

/** The API's name for a key inside a JSON column, else the column's. */
export const changeFieldLabel = (change: ActivityFieldChange) =>
  change.fieldLabel ?? fieldLabel(change.field);

const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/** Stored values are text; show timestamps the way the rest of the app does. */
export const formatFieldValue = (value: string) =>
  ISO_DATE_TIME.test(value) && moment(value).isValid()
    ? moment(value).format('MMM D, YYYY h:mm A')
    : value;

/** The readable name of a stored code when the API has one (e.g. "Active" for "A"), else the value. */
export const changeValue = (change: ActivityFieldChange, side: 'old' | 'new') =>
  side === 'old' ? change.oldLabel ?? change.oldValue : change.newLabel ?? change.newValue;

/** Field names only, for tooltips. */
export const changedFieldNames = (event: ActivityEvent) =>
  event.changes.map(changeFieldLabel).join(', ');

export const isFailure = (event: ActivityEvent) => event.outcome !== 'SUCCESS';

export const formatEventTime = (value: string) => moment(value).format('MMM D, h:mm A');

/** A column of the activity table: the fixed ones and the optional ones the user chose. */
export interface ActivityTableColumn {
  key: 'time' | 'actor' | 'activity' | ActivityColumn;
  label: string;
}

/** The activity table's columns, in display order; the export writes the same ones. */
export const activityTableColumns = (
  visible: ActivityColumn[],
  actorLabel: string,
): ActivityTableColumn[] => {
  const optional = (key: ActivityColumn): ActivityTableColumn[] =>
    visible.includes(key)
      ? [{ key, label: OPTIONAL_COLUMNS.find((column) => column.key === key)?.label ?? key }]
      : [];
  return [
    { key: 'time', label: 'Time' },
    { key: 'actor', label: actorLabel },
    ...optional('matter'),
    { key: 'activity', label: 'Activity' },
    ...optional('entity'),
    ...optional('record'),
    ...optional('outcome'),
  ];
};

/** What a cell of the table shows, as text (the time with its year). */
const cellText = (event: ActivityEvent, key: ActivityTableColumn['key']): string => {
  switch (key) {
    case 'time':
      return moment(event.occurredAt).format('MMM D, YYYY h:mm A');
    case 'actor':
      return event.actorName;
    case 'matter':
      return event.matter ? `#${event.matter.referenceId}` : '';
    case 'activity':
      return isFailure(event) ? `${activityTitle(event)} (failed)` : activityTitle(event);
    case 'entity':
      return event.subjectType ? entityName(event.subjectType) : '';
    case 'record':
      return event.subjectLabel ?? '';
    case 'outcome':
      return isFailure(event) ? event.outcome : 'Success';
  }
};

/** A CSV cell: quoted when needed; a leading =, +, -, @ is escaped so it never runs as a formula. */
const csvCell = (value: string) => {
  const text = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** The CSV of what the table shows: its columns, in its order, with the same text. */
export const buildActivityCsv = (events: ActivityEvent[], columns: ActivityTableColumn[]) =>
  [
    columns.map((column) => column.label),
    ...events.map((event) => columns.map((column) => cellText(event, column.key))),
  ]
    .map((row) => row.map(csvCell).join(','))
    .join('\n');

export const APP_LABELS: Record<string, string> = {
  FO: 'Front office web',
  BO: 'Back office',
  MOBILE: 'Claimant app',
  PORTAL: 'Claimant portal',
  API: 'API',
  JOB: 'Background job',
};

export interface ActivitySessionGroup {
  key: string;
  start: string;
  end: string;
  app: string;
  events: ActivityEvent[];
}

/** Groups a person's events (newest first) into their sessions, as in the timeline. */
export const groupBySession = (events: ActivityEvent[]): ActivitySessionGroup[] => {
  const groups: ActivitySessionGroup[] = [];
  for (const event of events) {
    const key = event.sessionId ?? `day:${moment(event.occurredAt).format('YYYY-MM-DD')}`;
    const current = groups[groups.length - 1];
    if (current && current.key === key) {
      current.events.push(event);
      current.start = event.occurredAt;
    } else {
      groups.push({
        key,
        start: event.occurredAt,
        end: event.occurredAt,
        app: APP_LABELS[event.app ?? ''] ?? '',
        events: [event],
      });
    }
  }
  return groups;
};

export const sessionDuration = (group: ActivitySessionGroup) => {
  const minutes = Math.max(1, moment(group.end).diff(moment(group.start), 'minutes'));
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

/** The page opens on the last 30 days; the API allows at most 90 days per query. */
export const MAX_RANGE_DAYS = 90;

export const defaultDateRange = () => ({
  from: moment().subtract(30, 'days').startOf('day').toDate(),
  to: moment().add(1, 'minute').toDate(),
});

export const toDateInput = (date: Date) => moment(date).format('YYYY-MM-DD');
